import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './SiteShell.css';
import { useMenuClose } from '../hooks/useMenuClose';
import { useAuth } from '../auth/AuthContext';
import AuthScreen from '../auth/AuthScreen';
import UserMenu from './UserMenu';

const guestBanner = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap',
  padding: '8px 16px', fontSize: 13, lineHeight: 1.4, textAlign: 'center',
  background: 'rgba(196,155,99,0.14)', borderBottom: '1px solid rgba(196,155,99,0.4)',
};
const guestBadge = {
  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11,
  color: '#1a1a1a', background: '#c49b63', borderRadius: 4, padding: '2px 7px',
};
const guestBannerBtn = {
  border: 'none', background: '#c49b63', color: '#1a1a1a',
  fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '5px 14px', cursor: 'pointer',
};

function GuestBanner() {
  const { isGuest, guestTtlDays } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  if (!isGuest) return null;
  return (
    <div style={guestBanner}>
      <span style={guestBadge}>Guest mode</span>
      <span>
        You're exploring without an account. Your trips will be <strong>deleted after {guestTtlDays} days</strong>{' '}
        unless you sign in to save them.
      </span>
      <button type="button" style={guestBannerBtn} onClick={() => setShowAuth(true)}>Sign in to save</button>
      {showAuth && <AuthScreen onClose={() => setShowAuth(false)} />}
    </div>
  );
}

const pausedBanner = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap',
  padding: '8px 16px', fontSize: 13, lineHeight: 1.4, textAlign: 'center',
  background: 'rgba(225,87,89,0.12)', borderBottom: '1px solid rgba(225,87,89,0.4)',
};

function PausedBanner() {
  const { apiPaused, apiPausedUntil } = useAuth();
  if (!apiPaused) return null;
  return (
    <div style={pausedBanner}>
      <span>
        🅿️ Maps &amp; a few live features are <strong>paused for the month</strong> to keep the app free during beta
        {apiPausedUntil ? <> — back on {apiPausedUntil}</> : null}. Everything else still works.
      </span>
    </div>
  );
}

function NavLink({ to, children, onClick }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      className={isActive ? 'tp-navlink tp-navlink--active' : 'tp-navlink'}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function GearMenu() {
  const { isOwner } = useAuth();
  const [open, setOpen] = useState(false);
  useMenuClose(open, () => setOpen(false));

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className={open ? 'tp-navlink tp-navlink--active tp-navlink--btn' : 'tp-navlink tp-navlink--btn'}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        aria-label="Settings"
        title="Settings"
      >
        ⚙
      </button>
      {open && (
        <div
          className="tp-card tp-card--tight tp-menu"
          style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', minWidth: 150, zIndex: 100 }}
          onClick={(e) => e.stopPropagation()}
        >
          <NavLink to="/database" onClick={() => setOpen(false)}>Database</NavLink>
          {isOwner && <NavLink to="/admin" onClick={() => setOpen(false)}>Admin</NavLink>}
        </div>
      )}
    </div>
  );
}

export default function SiteShell({ children }) {
  return (
    <div className="tp-shell">
      <header className="tp-header">
        <div className="tp-container tp-header__inner">
          <Link to="/" className="tp-brand">
            <span className="tp-brand__mark" aria-hidden="true">TP</span>
            <span className="tp-brand__text">Travel Planner</span>
          </Link>
          <nav className="tp-nav">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/trips">Manage Trips</NavLink>
            <GearMenu />
            <UserMenu />
          </nav>
        </div>
      </header>

      <GuestBanner />
      <PausedBanner />

      <main className="tp-main">
        <div className="tp-container">{children}</div>
      </main>

      <footer className="tp-footer">
        <div className="tp-container tp-footer__inner">
          <div className="tp-footer__muted">Travel Planner</div>
        </div>
      </footer>
    </div>
  );
}
